import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, authHeader, formatApiError } from "@/lib/api";
import { aud } from "@/lib/catalog";
import { Logo } from "@/components/pow/Logo";

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("pow_admin_token", data?.token ?? "");
      onDone();
    } catch (err) {
      setError(formatApiError(err));
    }
  };
  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="mx-auto mt-24 w-full max-w-sm space-y-4 rounded-[28px] bg-white p-8 shadow-xl">
      <h1 className="font-display text-4xl font-black uppercase text-[#112E23]">Orders admin</h1>
      <Input data-testid="admin-email-input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-12 rounded-xl" />
      <Input data-testid="admin-password-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-12 rounded-xl" />
      {error && <p data-testid="admin-login-error" className="text-sm text-[#a3260f]">{error}</p>}
      <button data-testid="admin-login-button" className="btn-dark w-full justify-center">Sign in</button>
    </form>
  );
};

const StatusSelect = ({ order, statuses, onChange }) => (
  <Select value={order.status} onValueChange={(v) => onChange(order.id, v)}>
    <SelectTrigger data-testid={`admin-order-status-select-${order.ref}`} className="h-9 w-[170px] rounded-full text-xs"><SelectValue /></SelectTrigger>
    <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
  </Select>
);

const Orders = ({ onLogout }) => {
  const [orders, setOrders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/orders", { headers: authHeader() });
      setOrders(Array.isArray(data?.orders) ? data.orders : []);
      setStatuses(Array.isArray(data?.statuses) ? data.statuses : []);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      else toast.error(formatApiError(err));
    }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);

  const update = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/orders/${id}`, { status }, { headers: authHeader() });
      setOrders((o) => o.map((x) => (x.id === id ? data : x)));
      toast.success(`${data?.ref} → ${status}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mt-10">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-5xl font-black uppercase text-[#112E23]">Orders <span className="font-mono text-lg text-[#6E472B]">({orders.length})</span></h1>
        <div className="flex gap-2">
          <button data-testid="admin-refresh-button" onClick={load} className="btn-ghost"><RefreshCw className="h-4 w-4" /> Refresh</button>
          <button data-testid="admin-logout-button" onClick={onLogout} className="btn-ghost"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-[24px] bg-white shadow-sm">
        <table data-testid="admin-orders-table" className="w-full min-w-[900px] text-left text-sm">
          <thead className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#112E23]/70">
            <tr className="border-b border-[#112E23]/10">{["Ref", "Date", "Customer", "Deliver to", "Items", "Total", "Status"].map((h) => <th key={h} className="px-4 py-4 font-normal">{h}</th>)}</tr>
          </thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-[#112E23]/70">No orders yet.</td></tr>}
            {orders.map((o) => (
              <tr key={o.id} data-testid={`admin-order-row-${o.ref}`} className="border-b border-[#112E23]/5 align-top">
                <td className="px-4 py-4 font-mono font-semibold">{o.ref}</td>
                <td className="px-4 py-4 font-mono text-xs">{new Date(o.created_at).toLocaleString("en-AU")}</td>
                <td className="px-4 py-4"><div className="font-semibold">{o.customer?.name}</div><div className="text-xs text-[#112E23]/70">{o.customer?.email}<br />{o.customer?.phone}</div></td>
                <td className="px-4 py-4 text-xs">{o.customer?.address}<br />{o.customer?.suburb} {o.customer?.state} {o.customer?.postcode}{o.notes && <div className="mt-1 italic text-[#6E472B]">“{o.notes}”</div>}</td>
                <td className="px-4 py-4 text-xs">{(o.items ?? []).map((i) => <div key={i.product_id}>{i.qty} × {i.colour}</div>)}</td>
                <td className="px-4 py-4 font-mono">{aud(o.total)}<div className="text-[11px] text-[#112E23]/60">incl. {aud(o.postage)} post</div></td>
                <td className="px-4 py-4"><StatusSelect order={o} statuses={statuses} onChange={update} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(() => !!localStorage.getItem("pow_admin_token"));
  const logout = useCallback(() => { localStorage.removeItem("pow_admin_token"); setAuthed(false); }, []);
  return (
    <div className="min-h-screen bg-[#F5F0E8] px-5 py-8 font-body text-[#112E23] sm:px-8">
      <div className="mx-auto max-w-[1300px]">
        <Link to="/" data-testid="admin-home-link"><Logo /></Link>
        {authed ? <Orders onLogout={logout} /> : <Login onDone={() => setAuthed(true)} />}
      </div>
    </div>
  );
}
